import React, { useState, useEffect, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import FileDropzone from './components/FileDropzone';
import ExpenseTable from './components/ExpenseTable';
import CategoryRules from './components/CategoryRules';
import ExpenseSummary from './components/ExpenseSummary';
import DataVisualizations from './components/DataVisualizations';
import BudgetManager from './components/BudgetManager';
import DateRangeSelector from './components/DateRangeSelector';
import Header from './components/Header';
import { CitiTransaction, ExpenseEntry, CategoryRule, BudgetItem, RuleMatchType, BUSINESS_CATEGORIES, EXPENSE_COGS_TYPES } from './types';
import { 
  parseFile,
  transformTransactions,
  generateExcelFile,
  merchantMappings
} from './utils/csvUtils';
import { isWithinInterval, parseISO } from 'date-fns';
import TagManager from './components/TagManager';

// Keys for storing data in localStorage
const RULES_STORAGE_KEY = 'expense-processor-rules';
const BUDGETS_STORAGE_KEY = 'expense-processor-budgets';
const EXPENSES_STORAGE_KEY = 'expense-processor-expenses';

// Generate default category rules from merchant mappings
const generateDefaultCategoryRules = (): CategoryRule[] => {
  return Object.entries(merchantMappings).map(([pattern, mapping]) => ({
    pattern,
    category: mapping.category,
    expenseCOGS: mapping.expenseCOGS,
    matchType: RuleMatchType.CONTAINS,
    caseSensitive: false
  }));
};

const App: React.FC = () => {
  const [originalTransactions, setOriginalTransactions] = useState<CitiTransaction[]>([]);
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<ExpenseEntry[]>([]);
  const [categoryRules, setCategoryRules] = useState<CategoryRule[]>(generateDefaultCategoryRules());
  const [budgets, setBudgets] = useState<BudgetItem[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{start: Date, end: Date} | null>(null);
  const [activeTab, setActiveTab] = useState<'data' | 'summary' | 'budget' | 'rules'>('data');

  // Load saved data from localStorage on initial load
  useEffect(() => {
    // Load rules
    const savedRules = localStorage.getItem(RULES_STORAGE_KEY);
    if (savedRules) {
      try {
        setCategoryRules(JSON.parse(savedRules));
      } catch (error) {
        console.error('Error loading saved rules:', error);
      }
    }
    
    // Load budgets
    const savedBudgets = localStorage.getItem(BUDGETS_STORAGE_KEY);
    if (savedBudgets) {
      try {
        setBudgets(JSON.parse(savedBudgets));
      } catch (error) {
        console.error('Error loading saved budgets:', error);
      }
    }
    
    // Load expenses
    const savedExpenses = localStorage.getItem(EXPENSES_STORAGE_KEY);
    if (savedExpenses) {
      try {
        const parsedExpenses = JSON.parse(savedExpenses);
        setExpenses(parsedExpenses);
        setFilteredExpenses(parsedExpenses);
      } catch (error) {
        console.error('Error loading saved expenses:', error);
      }
    }
  }, []);

  // Apply date filtering when expenses or date range changes
  useEffect(() => {
    if (!selectedDateRange) {
      setFilteredExpenses(expenses);
      return;
    }
    
    const { start, end } = selectedDateRange;
    
    // Filter expenses by date range
    const filtered = expenses.filter((expense) => {
      const dateParts = expense.date.split('/');
      if (dateParts.length !== 3) return false;
      
      const expenseDate = new Date(
        parseInt(dateParts[2]), // Year
        parseInt(dateParts[0]) - 1, // Month (0-indexed)
        parseInt(dateParts[1]) // Day
      );
      
      return expenseDate >= start && expenseDate <= end;
    });
    
    setFilteredExpenses(filtered);
  }, [expenses, selectedDateRange]);

  // Save expenses whenever they change
  useEffect(() => {
    if (expenses.length > 0) {
      localStorage.setItem(EXPENSES_STORAGE_KEY, JSON.stringify(expenses));
    }
  }, [expenses]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    setIsProcessing(true);
    try {
      setFileName(file.name);
      const result = await parseFile(file);
      setOriginalTransactions(result);
      
      // Transform transactions to expense entries
      const newExpenses = transformTransactions(result, categoryRules);
      
      // Check if this appears to be a Citi statement
      const isCitiStatement = file.name.toLowerCase().includes('citi') || 
                             (result.length > 0 && 'Status' in result[0] && 'Debit' in result[0]);
      
      // Merge new expenses with existing ones, avoiding duplicates
      setExpenses(prevExpenses => {
        // Create a map of existing expenses for faster lookup
        const existingMap = new Map(
          prevExpenses.map(expense => [
            `${expense.date}-${expense.description}-${expense.amount}`,
            true
          ])
        );
        
        // Filter out duplicates
        const uniqueNewExpenses = newExpenses.filter(
          newExpense => 
            !existingMap.has(`${newExpense.date}-${newExpense.description}-${newExpense.amount}`)
        );
        
        // Show success message with count
        if (uniqueNewExpenses.length > 0) {
          alert(`Successfully imported ${uniqueNewExpenses.length} new transactions${isCitiStatement ? ' from Citi statement' : ''}.`);
        } else {
          alert('No new transactions to import. All entries already exist.');
        }
        
        return [...prevExpenses, ...uniqueNewExpenses];
      });
      
      // If we had rules from previous processing, save them
      if (categoryRules.length > 0) {
        localStorage.setItem(RULES_STORAGE_KEY, JSON.stringify(categoryRules));
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setIsProcessing(false);
      alert('Error processing file. Please check the format and try again.');
    }
  };

  const handleUpdateExpense = (index: number, updatedExpense: ExpenseEntry) => {
    const updatedExpenses = [...expenses];
    updatedExpenses[index] = updatedExpense;
    setExpenses(updatedExpenses);
  };

  const handleUpdateRules = (rules: CategoryRule[]) => {
    setCategoryRules(rules);
    localStorage.setItem(RULES_STORAGE_KEY, JSON.stringify(rules));
    
    // Reapply rules to transactions
    if (originalTransactions.length > 0) {
      const updatedExpenses = transformTransactions(originalTransactions, rules);
      setExpenses(updatedExpenses);
    }
  };

  const handleAddCategoryRule = (rule: CategoryRule) => {
    const updatedRules = [...categoryRules, rule];
    handleUpdateRules(updatedRules);
  };

  const handleSaveBudgets = (updatedBudgets: BudgetItem[]) => {
    setBudgets(updatedBudgets);
    localStorage.setItem(BUDGETS_STORAGE_KEY, JSON.stringify(updatedBudgets));
  };

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setSelectedDateRange({ start: startDate, end: endDate });
  };

  const getExportFileName = () => {
    if (fileName) {
      return `processed_${fileName.replace('.csv', '')}.csv`;
    }
    return 'processed_expenses.csv';
  };

  const getExcelFileName = () => {
    if (fileName) {
      return `processed_${fileName.replace('.csv', '')}.xlsx`;
    }
    return 'processed_expenses.xlsx';
  };

  const handleExcelExport = () => {
    const dataToExport = filteredExpenses.length > 0 ? filteredExpenses : expenses;
    const blob = generateExcelFile(dataToExport, BUSINESS_CATEGORIES);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getExcelFileName());
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all expense data? This cannot be undone.')) {
      setExpenses([]);
      setFilteredExpenses([]);
      setOriginalTransactions([]);
      localStorage.removeItem(EXPENSES_STORAGE_KEY);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header 
        activeTab={activeTab} 
        onChangeTab={setActiveTab} 
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'data' && (
          <div>
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900">Upload Statement</h2>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Upload a Citi credit card statement CSV or Excel file with expenses to process.</p>
                </div>
                <div className="mt-5">
                  <FileDropzone 
                    onFileUpload={handleFileUpload} 
                    isLoading={isProcessing}
                  />
                </div>
              </div>
            </div>

            {expenses.length > 0 && (
              <div className="bg-white shadow rounded-lg mb-6">
                <div className="px-4 py-5 sm:p-6">
                  <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Processed Expenses
                      </h3>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        {filteredExpenses.length} of {expenses.length} expenses shown
                      </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <DateRangeSelector onDateRangeChange={handleDateRangeChange} />

                      <div className="inline-flex shadow-sm rounded-md">
                        <CSVLink 
                          data={filteredExpenses} 
                          filename={getExportFileName()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-l-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Download CSV
                        </CSVLink>
                        <button
                          type="button"
                          onClick={handleExcelExport}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          Download Excel
                        </button>
                      </div>
                      
                      <button
                        type="button"
                        onClick={handleClearData}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Clear Data
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <ExpenseTable 
                      expenses={filteredExpenses} 
                      onUpdateExpense={handleUpdateExpense}
                      onAddCategoryRule={handleAddCategoryRule}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'summary' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Expense Summary</h2>
              {filteredExpenses.length > 0 ? (
                <>
                  <ExpenseSummary expenses={filteredExpenses} />
                  <div className="mt-8">
                    <DataVisualizations expenses={filteredExpenses} />
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Upload a statement to view expense summaries.</p>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'budget' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Budget Manager</h2>
              <BudgetManager
                savedBudgets={budgets}
                expenses={filteredExpenses}
                onSaveBudgets={handleSaveBudgets}
              />
            </div>
          </div>
        )}
        
        {activeTab === 'rules' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Category Rules</h2>
              <CategoryRules
                rules={categoryRules}
                onUpdateRules={handleUpdateRules}
              />
            </div>
          </div>
        )}
      </main>
      
      <footer className="bg-white mt-8 py-4 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Credit Card Statement Processor with Expense/COGS Categorization
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App; 